/**
 * RelationPanels — il gruppo di pannelli relazione della colonna destra,
 * montabile da qualsiasi detail panel di qualsiasi plugin con una riga.
 *
 * ```tsx
 * <RelationPanels source={{ type: 'Contract', id: contract.contractId }} />
 * <RelationPanels source={{ type: 'Contract', id }} targets={['Person', 'Business']} />
 * ```
 *
 * Incapsula quello che prima era ~50 righe di JSX ricopiate in ogni detail:
 * fetch e cleanup delle relazioni, `RelationPanelGroup`, discovery dei target,
 * mappa tipo->card, wiring del "crea da contesto" dei plugin, e i titoli
 * localizzati.
 *
 * **Discovery.** I target sono le entita' che si sono dichiarate tali con
 * `relationTarget: true` (core e plugin allo stesso modo), limitate ai plugin
 * abilitati: spegnere un plugin fa sparire i suoi pannelli da solo.
 *
 * **`targets` e' un filtro, non un override.** Un target chiesto ma non
 * disponibile (plugin spento o non autorizzato) semplicemente non si
 * renderizza — quindi elencare `'Quotation'` resta sicuro anche dove il plugin
 * quotation non c'e': degrada da se'. Omettere `targets` significa invece
 * «tutto cio' che e' relazionabile», e un'entita' aggiunta domani compare da
 * sola: e' l'estendibilita', ma e' anche una porta aperta — si sceglie quando
 * la pagina e' un hub generico, non quando ha un layout studiato.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { EntityTypeDescriptor } from '@crm/contracts';
import { RelationPanel } from './RelationPanel';
import { RelationPanelGroup } from './RelationPanelGroup';
import { BusinessRelationCard } from './BusinessRelationCard';
import { QuotationRelationCard } from './QuotationRelationCard';
import { PersonRelationCard } from './PersonRelationCard';
import { useRelationTargetEntityTypes } from '../plugin/useEnabledEntityTypes';
import { entityTypeLabelPlural, getEntityFqn } from '../entityRegistry/entityRegistry';
import { useRelationshipsSource } from '../entities/relationship/useRelationshipsSource';
import type { RelationItemContext } from './RelationPanel';

export interface RelationPanelsProps {
  /** L'entita' di cui si stanno mostrando le relazioni. `type` e' il typeName corto. */
  source: { type: string; id: number };
  /**
   * Target da mostrare, nell'ordine dato. Omesso: tutti i target disponibili,
   * nell'ordine di registrazione nel registry.
   */
  targets?: string[];
  /**
   * Chiamata dopo ogni modifica delle relazioni (link, unlink, primary).
   * Serve ai consumer che derivano altro dalle relazioni — es. la timeline
   * attivita'. Il refresh dei pannelli avviene da solo via store.
   */
  onRelationChanged?: () => void;
}

/**
 * Card dedicate, lato core per scelta: `@crm/contracts` resta foglia e i
 * plugin non forniscono card custom. Tutto cio' che non e' qui usa la card
 * default del RelationPanel (link + descrizione).
 *
 * Le due card prendono `ctx` + l'id del target e sono gia' agnostiche rispetto
 * alla sorgente: fanno un fetch proprio per dominio/telefono/email.
 */
const CARD_BY_TYPE: Record<string, (ctx: RelationItemContext) => React.ReactNode> = {
  Business: (ctx) => <BusinessRelationCard ctx={ctx} businessId={ctx.relationship.classPK2} />,
  Person: (ctx) => <PersonRelationCard ctx={ctx} personId={ctx.relationship.classPK2} />,
  Quotation: (ctx) => <QuotationRelationCard ctx={ctx} quotationId={ctx.relationship.classPK2} />,
};

export function RelationPanels({ source, targets, onRelationChanged }: RelationPanelsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const available = useRelationTargetEntityTypes();

  // Il componente possiede la fetch: nessun consumer deve dispatchare
  // fetchRelationships o clearRelationships per farlo funzionare. L'hook e'
  // idempotente e ref-contato, quindi montarne piu' d'uno sulla stessa
  // sorgente non moltiplica le chiamate.
  useRelationshipsSource(getEntityFqn(source.type), source.id);

  const panels = useMemo<EntityTypeDescriptor[]>(() => {
    if (!targets) return available;
    // `targets` ordina oltre che filtrare: si itera sulla lista richiesta, non
    // su quella disponibile. I tipi non disponibili cadono qui, in silenzio.
    const byName = new Map(available.map((et) => [et.typeName, et]));
    // I duplicati cadono tenendo la PRIMA occorrenza, così la posizione
    // richiesta dal chiamante è rispettata. Serve perché un consumer può
    // comporre `targets` da più fonti — una lista fissa più i tipi dei plugin
    // abilitati — e lo stesso typeName può arrivare da entrambe. Senza,
    // sarebbero due pannelli identici con la stessa `key`.
    const seen = new Set<string>();
    return targets
      .map((name) => byName.get(name))
      .filter((et): et is EntityTypeDescriptor => Boolean(et))
      .filter((et) => {
        if (seen.has(et.typeName)) return false;
        seen.add(et.typeName);
        return true;
      });
  }, [targets, available]);

  return (
    <RelationPanelGroup>
      {panels.map((et) => {
        // Il plugin dichiara (via createFromSource) se ha un workflow "crea da
        // contesto" per questa sorgente. Il core non conosce ne' i path ne' i
        // typeName dei plugin: chiama l'hook e usa il risultato. Se undefined
        // -> comportamento default (solo "+ Aggiungi").
        const createCfg = et.createFromSource?.({
          typeName: source.type,
          classPK: source.id,
        });
        return (
          <RelationPanel
            key={et.typeName}
            sourceType={source.type}
            sourceId={source.id}
            targetType={et.typeName}
            title={entityTypeLabelPlural(et, t, et.typeName)}
            onRelationChanged={onRelationChanged}
            renderItem={CARD_BY_TYPE[et.typeName]}
            onCreateClick={createCfg ? () => navigate(createCfg.url) : undefined}
            hideLinkExisting={createCfg?.hideLinkExisting}
          />
        );
      })}
    </RelationPanelGroup>
  );
}
